// deps
import { useCallback, useContext, useState } from "react";
import { useRouter } from "next/router";

// internal deps
import {
    GenericErrorContext,
    CartDataContext,
    SwiggyHeadersContext,
} from "@swiggy-private/portal-swiggy-one-subscription-context/dist";
import { AnalyticsContext } from "@swiggy-private/analytics";
import { getNestedValue } from "@swiggy-private/js-utils";

// analytics
import { clickApplyButtonEvent } from "analytics";

// apis
import { fetchCart } from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/api";

// utils
import { filterHeadersFromSwiggyHeaders } from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/utils";

//constants
import { NR_ALERTING_FOR_TYPIA } from "@swiggy-private/portal-swiggy-one-subscription-ui/dist/constants";
import { sendCustomNREvent } from "utils/utils";

const useApplyCouponOnClick = () => {
    const router = useRouter();
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [inputBoxError, setInputBoxError] = useState("");
    const { setCartData, setShowCouponModal } = useContext(CartDataContext);
    const { swiggyHeaders } = useContext(SwiggyHeadersContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const { setGenericError } = useContext(GenericErrorContext);

    const applyCouponOnClick = useCallback(
        (couponCode: string) => {
            async function applyCouponClick() {
                if (!swiggyHeaders) {
                    return;
                }
                try {
                    setShowBottomSheet(true);
                    const data: any = await fetchCart({ swiggyHeaders, sendCustomNREvent, couponCode });

                    // this try catch block is only used to monitor contract breaks in home response will not impact any user journeys
                    // we are loading this as dynamic import to run these checks in async manner
                    const { checkResponseTypesForCartPageData, isTypiaTypeGuardError } = (
                        await import("@swiggy-private/portal-swiggy-one-subscription-typia/dist")
                    ).default;

                    try {
                        checkResponseTypesForCartPageData(data);
                    } catch (err: any) {
                        if (isTypiaTypeGuardError(err)) {
                            sendCustomNREvent(
                                NR_ALERTING_FOR_TYPIA.CART,
                                { message: JSON.stringify(err?.message) },
                                swiggyHeaders,
                            );
                        }
                    }

                    if (data?.error?.code !== 0) {
                        const swgyHeaders = swiggyHeaders ? filterHeadersFromSwiggyHeaders(swiggyHeaders) : {};
                        setInputBoxError(data.error.message);
                        setShowBottomSheet(false);
                        sendCustomNREvent("portal_swiggy_one_subscription_coupon_invalid", { couponCode }, swgyHeaders);
                        clickApplyButtonEvent?.(
                            clickEvent,
                            JSON.stringify({ coupon_code: couponCode, coupon_state: "invalid" }),
                        );
                        return;
                    }
                    clickApplyButtonEvent?.(
                        clickEvent,
                        JSON.stringify({ coupon_code: couponCode, coupon_state: "valid" }),
                    );
                    setShowCouponModal(true);
                    setCartData(data);
                    sessionStorage.setItem("cart_data", JSON.stringify(data));
                    sessionStorage.setItem("items-quantity", getNestedValue(data, "cart.order_lines.0.quantity"));
                    sessionStorage.setItem(
                        "discount-message",
                        `Savings of ₹${getNestedValue(
                            data,
                            "cart.pricing_info.aggregated_pricing_info.pricing_total.total_discount.units",
                        )}`,
                    );
                    setShowBottomSheet(false);
                    router.push("/cart/");
                    return;
                } catch (err) {
                    setShowBottomSheet(false);
                    setGenericError(true);
                    return;
                }
            }
            applyCouponClick();
        },
        [clickEvent, router, setCartData, setShowCouponModal, swiggyHeaders, setGenericError],
    );

    return { applyCouponOnClick, showBottomSheet, inputBoxError, setInputBoxError };
};

export default useApplyCouponOnClick;
